/**
 * Customer review 11 Sep (V12): supplier-side NDA sign-off, cloned from
 * CodeOfConductModal. Signed once at onboarding against the customer's
 * current NDA version; when the customer uploads a new version the modal
 * offers it again.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  FileSignature,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { onboardingAPI } from '../../lib/api';
import type { NDA, NDAAgreement } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';

interface NDAModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The customer whose NDA is being signed. */
  customerId: number;
  customerName?: string;
  onSigned?: (agreement: NDAAgreement) => void;
}

export default function NDAModal({ open, onOpenChange, customerId, customerName, onSigned }: NDAModalProps) {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [nda, setNda] = useState<NDA | null>(null);
  const [existingAgreement, setExistingAgreement] = useState<NDAAgreement | null>(null);
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('');
  const [accepted, setAccepted] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await onboardingAPI.getNDAStatus(customerId);
      setNda(res.data.nda);
      setExistingAgreement(res.data.agreement);
    } catch (err: unknown) {
      console.error('Failed to fetch NDA status:', err);
      setError(getApiErrorMessage(err, 'Failed to load the NDA'));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) {
      setAccepted(false);
      setSignatoryName([user?.first_name, user?.last_name].filter(Boolean).join(' '));
      setSignatoryTitle('');
      fetchStatus();
    }
  }, [open, fetchStatus, user?.first_name, user?.last_name]);

  const handleSign = async () => {
    if (!nda) return;
    if (!signatoryName.trim()) { toast.error('Please enter your name'); return; }
    if (!accepted) { toast.error('Please confirm you have read and accept the NDA'); return; }
    setSigning(true);
    try {
      const response = await onboardingAPI.signNDA({
        nda_id: nda.id,
        signatory_name: signatoryName.trim(),
        signatory_title: signatoryTitle.trim(),
        accepted: true,
      });
      toast.success(`NDA signed for ${customerName || nda.company_name || 'the customer'}`);
      setExistingAgreement(response.data);
      onSigned?.(response.data);
      onOpenChange(false);
    } catch (err: unknown) {
      console.error('Failed to sign NDA:', err);
      toast.error(getApiErrorMessage(err, 'Failed to sign the NDA'));
    } finally {
      setSigning(false);
    }
  };

  const heading = customerName ? `${customerName} — NDA` : 'NDA';

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading NDA...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Could not load the NDA</h3>
            <p className="text-muted-foreground text-center">{error}</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={fetchStatus}>Retry</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!nda) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">No NDA to sign</h3>
            <p className="text-muted-foreground text-center">
              {customerName || 'This customer'} has not uploaded an NDA yet. Nothing is required from you.
            </p>
            <Button className="mt-4" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (existingAgreement) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              NDA signed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700">
                Your company has signed version {existingAgreement.nda_version} of this NDA.
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Document</p>
                <p className="font-medium">{nda.title}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Signed by</p>
                <p className="font-medium">
                  {existingAgreement.signatory_name}
                  {existingAgreement.signatory_title && ` - ${existingAgreement.signatory_title}`}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Signed on</p>
                <p className="font-medium">{formatDate(existingAgreement.signed_at)}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5" />
                {heading}
              </DialogTitle>
              <DialogDescription>
                Please read and sign {nda.title}. Signing is done once per version.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">Version {nda.version}</Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(nda.uploaded_at)}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <hr className="border-t border-gray-200" />

        <div className="p-4 border rounded-lg bg-muted/30 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium truncate">{nda.title}</p>
            <p className="text-xs text-muted-foreground">
              {nda.file_extension ? nda.file_extension.toUpperCase() : 'Document'} — open it to read before signing
            </p>
          </div>
          {nda.file_url ? (
            <Button asChild variant="outline" size="sm">
              <a href={nda.file_url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open document
              </a>
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">Document unavailable</span>
          )}
        </div>

        <hr className="border-t border-gray-200" />

        <div className="space-y-4 py-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="nda-accepted"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <Label htmlFor="nda-accepted" className="text-sm">
              I have read and accept the above NDA on behalf of my company
            </Label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nda-signatory-name">Your Name *</Label>
              <Input
                id="nda-signatory-name"
                value={signatoryName}
                onChange={(e) => setSignatoryName(e.target.value)}
                placeholder="Enter your full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nda-signatory-title">Your Title</Label>
              <Input
                id="nda-signatory-title"
                value={signatoryTitle}
                onChange={(e) => setSignatoryTitle(e.target.value)}
                placeholder="e.g., Managing Director"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={signing}>
            Cancel
          </Button>
          <Button onClick={handleSign} disabled={signing || !accepted} className="min-w-[120px]">
            {signing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                <FileSignature className="h-4 w-4 mr-2" />
                Sign NDA
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
